// Settings → AI models. Each customer brings their own OpenRouter account (one-click sign-in or a
// pasted key) and picks the model for each job: messaging, comments (+ optional image analysis)
// and captions. Picks save instantly; "Test" makes one tiny real call with the picked model.

import {
  AI_TASK_INFO,
  estimateActionCost,
  formatUsd,
  type AiModelsRes,
  type AiSettingsRes,
  type AiTask,
  type AiTestRes,
} from "@gmx/shared/features/ai";
import { Badge, Button, buttonClass, Callout, Card, Input, ProgressBar, Skeleton, Switch, useConfirm, useToast } from "@gmx/ui";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Clapperboard, ExternalLink, Eye, KeyRound, Plug, Sparkles, Zap } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { aiApi, useAiModels, useAiMutation, useAiSettings } from "./aiApi";
import { ModelPicker, per1k, REC_LABEL } from "./ModelPicker";
import "./ai.css";
import { StudioModels } from './StudioModels';
import { FalConnection } from './FalConnection';
import { usePlan } from "../../lib/plan";

const OPENROUTER_KEYS_URL = "https://openrouter.ai/settings/keys";
const OPENROUTER_CREDITS_URL = "https://openrouter.ai/settings/credits";

export function AiTab() {
  const settings = useAiSettings();
  const models = useAiModels();
  // The Studio sections live under /api/scheduler/studio/*: Scheduler is a plan feature, so hide them when it's locked.
  const studio = usePlan().has("scheduler");
  if (settings.isPending) return <Skeleton height={520} radius={10} />;
  if (settings.isError) {
    return (
      <Callout tone="danger" title="Couldn't load your AI settings" action={<Button size="sm" onClick={() => void settings.refetch()}>Try again</Button>}>
        {settings.error instanceof Error ? settings.error.message : null}
      </Callout>
    );
  }
  return (
    <div className="gf-stack gfp-ai">
      <ConnectionCard s={settings.data} />
      {studio ? <FalConnection /> : null}
      <ModelsCard s={settings.data} catalog={models.data} catalogLoading={models.isPending} catalogError={models.error} />
      {studio ? <StudioModels /> : null}
    </div>
  );
}

// ── Connection ────────────────────────────────────────────────────────────────

function ConnectionCard({ s }: { s: AiSettingsRes }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [pasting, setPasting] = useState(false);
  const [key, setKey] = useState("");
  const start = useMutation({ mutationFn: aiApi.startOAuth, onSuccess: ({ url }) => window.location.assign(url) });
  const saveKey = useAiMutation(aiApi.saveKey);
  const disconnect = useAiMutation(aiApi.disconnect);
  const c = s.connection;

  async function onSaveKey() {
    try {
      await saveKey.mutateAsync(key.trim());
      setKey("");
      setPasting(false);
      toast.success("OpenRouter connected", "Your AI now runs on your own account.");
    } catch (err) {
      toast.error(err);
    }
  }

  function connect() {
    start.mutate(undefined, { onError: (err) => toast.error(err) });
  }

  const keyForm = pasting ? (
    <form
      className="gfp-ai__keyform"
      onSubmit={(e) => {
        e.preventDefault();
        void onSaveKey();
      }}
    >
      <Input
        type="password"
        autoComplete="off"
        spellCheck={false}
        placeholder="sk-or-v1-…"
        aria-label="OpenRouter API key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        autoFocus
      />
      <Button type="submit" variant="primary" loading={saveKey.isPending} disabled={!key.trim()}>
        Save key
      </Button>
      <Button type="button" variant="ghost" onClick={() => setPasting(false)}>
        Cancel
      </Button>
      <p className="gfp-ai__hint">
        Create one at{" "}
        <a href={OPENROUTER_KEYS_URL} target="_blank" rel="noreferrer">
          openrouter.ai/settings/keys
        </a>
        . We check it with OpenRouter, then store it encrypted. It's never shown again.
      </p>
    </form>
  ) : null;

  if (!c.connected) {
    return (
      <Card accent title="Connect your OpenRouter account" subtitle="One account, every major AI model. You pay OpenRouter directly at their prices; Grow My X adds nothing on top.">
        <div className="gf-stack">
          <ol className="gfp-ai__steps">
            <li>Sign in to OpenRouter (or create a free account) and approve Grow My X.</li>
            <li>Add a few dollars of credit on OpenRouter. With the cheap models below, most accounts spend well under $5 a month.</li>
            <li>Pick a model for each job below. We've marked the best one and the cheapest one that does the job well.</li>
          </ol>
          <FallbackNote s={s} />
          <div className="gfp-ai__actions">
            <Button variant="primary" icon={<Plug size={16} />} loading={start.isPending} onClick={connect}>
              Connect OpenRouter
            </Button>
            {!pasting ? (
              <Button variant="ghost" icon={<KeyRound size={16} />} onClick={() => setPasting(true)}>
                Paste an API key instead
              </Button>
            ) : null}
          </div>
          {keyForm}
        </div>
      </Card>
    );
  }

  const credits = c.credits;
  return (
    <Card
      accent
      title={
        <span className="gfp-ai__title">
          OpenRouter{" "}
          <Badge tone="success" dot>
            Connected
          </Badge>
        </span>
      }
      subtitle={c.method === "oauth" ? "Connected by signing in to OpenRouter. Every AI call uses your account." : "Connected with your API key. Every AI call uses your account."}
      actions={
        <a className={buttonClass({ variant: "secondary", size: "sm" })} href={OPENROUTER_CREDITS_URL} target="_blank" rel="noreferrer">
          Add credits <ExternalLink size={14} aria-hidden="true" />
        </a>
      }
    >
      <div className="gf-stack">
        {c.problem ? (
          <Callout tone="danger" title="This connection stopped working" action={<Button size="sm" variant="primary" loading={start.isPending} onClick={connect}>Connect again</Button>}>
            {c.problem}
          </Callout>
        ) : null}
        <dl className="gfp-ai__facts">
          <Fact label="Key">
            <code>{c.keyHint ?? "—"}</code>
            {c.label ? <span className="gfp-ai__muted"> · {c.label}</span> : null}
          </Fact>
          {c.connectedAt ? <Fact label="Connected">{new Date(c.connectedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}</Fact> : null}
          {credits ? (
            <Fact label="Spent on this key">
              {formatUsd(credits.usage) === "Free" ? "$0.00" : formatUsd(credits.usage)}
              {credits.freeTier ? <span className="gfp-ai__muted"> · free tier: add credits to use paid models</span> : null}
            </Fact>
          ) : null}
        </dl>
        {credits?.limit ? (
          <ProgressBar
            value={credits.usage}
            max={credits.limit}
            label="Spending limit on this key"
            valueLabel={`${formatUsd(credits.usage)} of ${formatUsd(credits.limit)}`}
            size="sm"
          />
        ) : null}
        <div className="gfp-ai__actions">
          {!pasting ? (
            <Button variant="ghost" size="sm" icon={<KeyRound size={15} />} onClick={() => setPasting(true)}>
              Use a different key
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void confirm({
                title: "Disconnect OpenRouter?",
                description:
                  "AI features stop until you connect again. Your model picks are kept. To revoke the key itself, delete it on openrouter.ai/settings/keys.",
                confirmLabel: "Disconnect",
                tone: "danger",
                action: () => disconnect.mutateAsync(undefined),
              })
            }
          >
            Disconnect
          </Button>
        </div>
        {keyForm}
      </div>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="gfp-ai__fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function FallbackNote({ s }: { s: AiSettingsRes }) {
  if (s.fallback === "none") {
    return (
      <Callout tone="warn" title="AI is off until you connect">
        DMs go out as your templates, Comment AI sorts comments with keyword rules only, Engage skips AI comments, and reply suggestions are unavailable.
      </Callout>
    );
  }
  if (s.fallback === "mock") {
    return (
      <Callout tone="info" title="Demo mode">
        Until you connect, AI features write sample text so you can try everything out.
      </Callout>
    );
  }
  return (
    <Callout tone="info" title="Using Grow My X's shared AI for now">
      Connect your own account to choose your models and keep your AI separate.
    </Callout>
  );
}

// ── Models ────────────────────────────────────────────────────────────────────

interface ModelsCardProps {
  s: AiSettingsRes;
  catalog: AiModelsRes | undefined;
  catalogLoading: boolean;
  catalogError: Error | null;
}

function ModelsCard({ s, catalog, catalogLoading, catalogError }: ModelsCardProps) {
  const toast = useToast();
  const save = useAiMutation(aiApi.save);
  const [savingTask, setSavingTask] = useState<AiTask | null>(null);

  async function pick(task: AiTask, id: string) {
    setSavingTask(task);
    try {
      const res = await save.mutateAsync({ models: { [task]: id } });
      toast.success(`${res.models[task]?.name ?? id} saved`, `It now handles ${AI_TASK_INFO[task].label.toLowerCase()}.`);
    } catch (err) {
      toast.error(err);
    } finally {
      setSavingTask(null);
    }
  }

  async function toggleImages(on: boolean) {
    // First time on: start with the cheapest model that can see images, so it's never "unset".
    const starter = on && !s.models.vision ? catalog?.recommendations.vision.find((r) => r.kind === "cheapest")?.modelId : undefined;
    try {
      await save.mutateAsync({ analyzeImages: on, ...(starter ? { models: { vision: starter } } : {}) });
      toast.success(on ? "Image analysis on" : "Image analysis off", on ? "Comments now look at the post photo first." : "Comments work from the caption only.");
    } catch (err) {
      toast.error(err);
    }
  }

  const row = (task: AiTask, extra?: ReactNode) => (
    <TaskRow
      key={task}
      task={task}
      s={s}
      catalog={catalog}
      catalogLoading={catalogLoading}
      saving={savingTask === task}
      onPick={(id) => void pick(task, id)}
    >
      {extra}
    </TaskRow>
  );

  const visionModel = catalog?.models.find((m) => m.id === s.models.vision?.id);
  const commentsModel = catalog?.models.find((m) => m.id === s.models.comments?.id);
  const extraPerK =
    visionModel && commentsModel ? Math.max(0, (estimateActionCost(visionModel, "vision") - estimateActionCost(commentsModel, "comments")) * 1000) : null;

  return (
    <Card
      title="Models"
      subtitle="Pick the model Grow My X uses for each job. Changes save right away."
      footer={
        <span className="gfp-ai__muted">
          AI calls today: {s.callsToday.toLocaleString()} of {s.dailyCap.toLocaleString()} (resets at midnight UTC).
        </span>
      }
    >
      <div className="gfp-ai__tasks">
        {catalogError ? (
          <Callout tone="warn" title="Couldn't load OpenRouter's model list">
            {catalogError.message} Your current picks still work.
          </Callout>
        ) : null}
        {row("messaging")}
        {row(
          "comments",
          <div className="gfp-ai__vision">
            <Switch
              checked={s.analyzeImages}
              onChange={(on) => void toggleImages(on)}
              disabled={save.isPending}
              label={
                <span className="gfp-ai__title">
                  <Eye size={16} aria-hidden="true" /> Look at the post image
                </span>
              }
              description={
                <>
                  Before writing a reply or a comment, the AI looks at the post's photo (or a reel's cover), so it can react to what's actually in it, not just the
                  caption.
                  {extraPerK !== null ? ` Adds about ${formatUsd(extraPerK)} per 1,000 comments with the models picked here.` : ""} Spotlight has its own “Look
                  at the post first” switch on each automation.
                </>
              }
            />
          </div>,
        )}
        {/* Always shown: Spotlight, Auto-Caption and Profile Snapshots use it whether or not the switch above is on. */}
        {row("vision")}
        {row("video")}
        {row("content")}
      </div>
    </Card>
  );
}

interface TaskRowProps {
  task: AiTask;
  s: AiSettingsRes;
  catalog: AiModelsRes | undefined;
  catalogLoading: boolean;
  saving: boolean;
  onPick: (id: string) => void;
  children?: ReactNode;
}

function TaskRow({ task, s, catalog, catalogLoading, saving, onPick, children }: TaskRowProps) {
  const info = AI_TASK_INFO[task];
  const picked = s.models[task];
  const recs = catalog?.recommendations[task];
  const [result, setResult] = useState<AiTestRes | { error: string } | null>(null);
  // A test result belongs to the model it ran on.
  useEffect(() => setResult(null), [picked?.id]);
  const test = useMutation({
    mutationFn: () => aiApi.test(task),
    onMutate: () => setResult(null),
    onSuccess: (r) => setResult(r),
    onError: (err) => setResult({ error: err instanceof Error ? err.message : "The test failed." }),
  });
  const canTest = s.connection.connected || s.fallback === "mock";
  const pickedRec = recs?.find((r) => r.modelId === picked?.id);
  const quick = recs?.filter((r) => r.modelId !== picked?.id) ?? [];

  return (
    <section className="gfp-ai__task" aria-labelledby={`ai-task-${task}`}>
      <div className="gfp-ai__task-head">
        <h4 id={`ai-task-${task}`} className="gfp-ai__task-title">
          {task === "content" ? (
            <Sparkles size={16} aria-hidden="true" />
          ) : task === "vision" ? (
            <Eye size={16} aria-hidden="true" />
          ) : task === "video" ? (
            <Clapperboard size={16} aria-hidden="true" />
          ) : (
            <Zap size={16} aria-hidden="true" />
          )}
          {info.label}
        </h4>
        <p className="gfp-ai__task-desc">{info.description}</p>
        <p className="gfp-ai__muted">{info.usedFor}</p>
      </div>
      <div className="gfp-ai__task-pick">
        <ModelPicker
          task={task}
          label={`${info.label} model`}
          value={picked?.id ?? null}
          models={catalog?.models}
          recommendations={recs}
          loading={catalogLoading}
          saving={saving}
          onChange={onPick}
        />
        <Button variant="secondary" loading={test.isPending} disabled={!canTest || !picked} onClick={() => test.mutate()}>
          Test
        </Button>
      </div>
      {pickedRec ? (
        <p className="gfp-ai__ok">
          <CheckCircle2 size={14} aria-hidden="true" /> {pickedRec.why}
        </p>
      ) : null}
      {quick.length ? (
        <div className="gfp-ai__quick">
          {quick.map((r) => {
            const m = catalog?.models.find((x) => x.id === r.modelId);
            if (!m) return null;
            return (
              <button key={r.kind} type="button" className="gfp-ai__chip" disabled={saving} onClick={() => onPick(m.id)} title={r.why}>
                <span className="gfp-ai__chip-kind">{REC_LABEL[r.kind]}:</span> {m.name}
                <span className="gfp-ai__muted"> · {per1k(m, task)}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {result ? (
        "error" in result ? (
          <p className="gfp-ai__test gfp-ai__test--bad" role="status">
            {result.error}
          </p>
        ) : (
          <p className="gfp-ai__test" role="status">
            <CheckCircle2 size={14} aria-hidden="true" /> “{result.reply}” <span className="gfp-ai__muted">· {result.model} · {(result.ms / 1000).toFixed(1)}s</span>
          </p>
        )
      ) : null}
      {children}
    </section>
  );
}
